import { InputState, Rectangle } from '../../types/game.types';
import { BaseGame } from '../BaseGame';
import { RenderManager } from '../../core/RenderManager';
import { AudioManager } from '../../core/AudioManager';
import { Frog } from './entities/Frog';
import { Vehicle } from './entities/Vehicle';
import { LogTurtle } from './entities/LogTurtle';
import { Hazard, HazardType } from './entities/Hazard';
import { LaneManager, LaneConfig } from './systems/LaneManager';
import { GoalManager } from './systems/GoalManager';

export class RiverCrossingGame extends BaseGame {
  private frog: Frog | null = null;
  private laneManager: LaneManager;
  private goalManager: GoalManager;
  private vehicles: Vehicle[] = [];
  private logsTurtles: LogTurtle[] = [];
  private hazards: Hazard[] = [];
  
  // Bonus objects
  private ladyFrog: { x: number, y: number, active: boolean, timer: number } | null = null;
  private flies: { x: number, y: number, active: boolean, timer: number }[] = [];
  private readonly LADY_FROG_SPAWN_TIME = 30; // 30 seconds
  private readonly FLY_SPAWN_TIME = 15; // 15 seconds
  private ladyFrogTimer = 0;
  private flySpawnTimer = 0;
  
  // Game timing and state
  private gameTimer: number = 60; // 60 seconds per level
  private maxTimer: number = 60;
  private gameStarted: boolean = false;
  private currentRow: number = 13; // Start at bottom (row 13)
  private highestRowReached: number = 13;
  
  // Game constants
  private readonly GRID_SIZE: number = 32;
  private readonly GRID_COLS: number = 15;
  private readonly GRID_ROWS: number = 14;
  private readonly GAME_WIDTH: number = 480; // 15 * 32
  private readonly GAME_HEIGHT: number = 448; // 14 * 32
  private readonly OFFSET_X: number = 160; // Center horizontally
  private readonly OFFSET_Y: number = 76;  // Center vertically
  
  // Scoring - Per Frogger manual
  private readonly BASE_POINTS = {
    FORWARD_HOP: 10,
    FROG_BAY: 50,
    TIME_BONUS: 10, // Per remaining time unit
    WAVE_COMPLETE: 1000,
    LADY_FROG: 200,
    FLY_CATCH: 200
  };
  
  // Extra life threshold
  private readonly EXTRA_LIFE_SCORE = 20000;
  private lastExtraLifeScore = 0;
  
  // Lane configuration (from bottom to top) - Slower speeds for level 1
  private readonly LANE_CONFIG: LaneConfig[] = [
    { type: 'safe', speed: 0 },      // Row 13 - Start area
    { type: 'road', speed: -1 },     // Row 12 - Cars right to left
    { type: 'road', speed: 0.8 },    // Row 11 - Cars left to right  
    { type: 'road', speed: -1.5 },   // Row 10 - Trucks right to left
    { type: 'road', speed: 1.2 },    // Row 9 - Cars left to right
    { type: 'road', speed: -1.3 },   // Row 8 - Cars right to left
    { type: 'safe', speed: 0 },      // Row 7 - Middle safe area
    { type: 'water', speed: 1 },     // Row 6 - Turtles left to right
    { type: 'water', speed: -1.2 },  // Row 5 - Logs right to left
    { type: 'water', speed: 0.8 },   // Row 4 - Turtles left to right
    { type: 'water', speed: -1.5 },  // Row 3 - Logs right to left
    { type: 'water', speed: 1.3 },   // Row 2 - Turtles left to right
    { type: 'water', speed: -0.9 },  // Row 1 - Logs right to left
    { type: 'goal', speed: 0 }       // Row 0 - Goal lily pads
  ];

  constructor(renderManager: RenderManager, audioManager: AudioManager) {
    const gameConfig = { width: 600, height: 900, fps: 60, scale: 1 }; // Portrait for mobile
    super(renderManager, audioManager, gameConfig, 'river-crossing');
    
    this.laneManager = new LaneManager(this.LANE_CONFIG, this.GRID_SIZE, this.GRID_COLS);
    this.goalManager = new GoalManager(this.GRID_COLS, renderManager);
  }

  public init(): void {
    console.log('River Crossing Game initialized');
    
    // Reset game state
    this.score = 0;
    this.lives = 7; // Official Frogger starts with 7 lives per manual
    this.level = 1;
    this.isGameOver = false;
    this.gameStarted = true;
    this.lastExtraLifeScore = 0;
    
    // Reset timer
    this.gameTimer = this.maxTimer;
    this.currentRow = 13;
    this.highestRowReached = 13;
    
    // Clear entities
    this.vehicles = [];
    this.logsTurtles = [];
    this.hazards = [];
    this.ladyFrog = null;
    this.flies = [];
    this.ladyFrogTimer = 0;
    this.flySpawnTimer = 0;
    
    // Create frog
    this.createFrog();
    
    // Initialize lanes and goals
    this.laneManager.reset();
    this.goalManager.reset();
    
    // Spawn initial traffic and river objects
    this.spawnInitialTraffic();
  }

  private createFrog(): void {
    const startX = Math.floor(this.GRID_COLS / 2) * this.GRID_SIZE;
    const startY = 13 * this.GRID_SIZE;
    
    this.frog = new Frog(
      'frog',
      startX,
      startY,
      this.renderManager,
      this.audioManager,
      this.GRID_SIZE
    );
  }

  private spawnInitialTraffic(): void {
    // Spawn vehicles on road lanes
    for (let row = 8; row <= 12; row++) {
      const laneConfig = this.LANE_CONFIG[13 - row]; // Convert to config index
      if (laneConfig.type === 'road') {
        this.spawnVehiclesForLane(row, laneConfig.speed);
      }
    }
    
    // Spawn logs and turtles on water lanes
    for (let row = 1; row <= 6; row++) {
      const laneConfig = this.LANE_CONFIG[13 - row];
      if (laneConfig.type === 'water') {
        this.spawnLogsTurtlesForLane(row, laneConfig.speed);
        
        // Spawn hazards on higher levels
        if (this.level >= 3) {
          this.spawnHazardsForLane(row, laneConfig.speed);
        }
      }
    }
  }

  private spawnVehiclesForLane(row: number, speed: number): void {
    const vehicleCount = 2 + Math.random() * 2; // 2-4 vehicles per lane
    const spacing = this.GAME_WIDTH / vehicleCount;
    
    for (let i = 0; i < vehicleCount; i++) {
      const x = i * spacing + Math.random() * spacing;
      const y = row * this.GRID_SIZE;
      
      // Determine vehicle type based on lane
      const isLargeVehicle = Math.random() < 0.3; // 30% chance for trucks
      
      const vehicle = new Vehicle(
        `vehicle_${row}_${i}`,
        x,
        y,
        speed,
        isLargeVehicle,
        this.renderManager,
        this.audioManager,
        this.GAME_WIDTH
      );
      
      this.vehicles.push(vehicle);
    }
  }

  private spawnLogsTurtlesForLane(row: number, speed: number): void {
    const objectCount = 2 + Math.random() * 2; // 2-4 objects per lane
    const spacing = this.GAME_WIDTH / objectCount;
    
    for (let i = 0; i < objectCount; i++) {
      const x = i * spacing + Math.random() * spacing;
      const y = row * this.GRID_SIZE;
      
      // Alternate between logs and turtles
      const isLog = (row % 2) === 1; // Odd rows = logs, even rows = turtles
      
      const logTurtle = new LogTurtle(
        `logturtle_${row}_${i}`,
        x,
        y,
        speed,
        isLog,
        this.renderManager,
        this.audioManager,
        this.GAME_WIDTH
      );
      
      this.logsTurtles.push(logTurtle);
    }
  }

  public update(deltaTime: number): void {
    if (this.isGameOver || !this.gameStarted || !this.frog) return;
    
    // Update timer
    this.gameTimer -= deltaTime;
    if (this.gameTimer <= 0) {
      this.loseLife();
      return;
    }
    
    // Update frog
    this.frog.update(deltaTime);
    
    // Update vehicles
    this.updateVehicles(deltaTime);
    
    // Update logs and turtles
    this.updateLogsTurtles(deltaTime);
    
    // Update hazards (snakes, otters, crocodiles)
    this.updateHazards(deltaTime);
    
    // Check collisions
    this.checkCollisions();
    
    // Check win conditions
    this.checkWinConditions();
    
    // Check for extra life
    this.checkExtraLife();
    
    // Update bonus objects
    this.updateBonusObjects(deltaTime);
    
    // Spawn new entities if needed
    this.spawnEntitiesAsNeeded();
  }

  private updateVehicles(deltaTime: number): void {
    this.vehicles.forEach(vehicle => {
      vehicle.update(deltaTime);
    });
    
    // Remove vehicles that are far off screen
    this.vehicles = this.vehicles.filter(vehicle => {
      const pos = vehicle.getPosition();
      return pos.x > -200 && pos.x < this.GAME_WIDTH + 200;
    });
  }

  private updateLogsTurtles(deltaTime: number): void {
    this.logsTurtles.forEach(logTurtle => {
      logTurtle.update(deltaTime);
      
      // If frog is riding this log/turtle, move frog with it
      if (this.frog && this.frog.isRiding()) {
        const ridingObject = this.frog.getRidingObject();
        if (ridingObject === logTurtle) {
          this.frog.moveWithRiding(logTurtle.getSpeed() * deltaTime, 0);
        }
      }
    });
    
    // Remove logs/turtles that are far off screen
    this.logsTurtles = this.logsTurtles.filter(logTurtle => {
      const pos = logTurtle.getPosition();
      return pos.x > -200 && pos.x < this.GAME_WIDTH + 200;
    });
  }
  
  private updateHazards(deltaTime: number): void {
    this.hazards.forEach(hazard => {
      hazard.update(deltaTime);
      
      // If frog is riding this hazard, move frog with it
      if (this.frog && this.frog.isRiding()) {
        const ridingObject = this.frog.getRidingObject();
        if (ridingObject === hazard) {
          this.frog.moveWithRiding(hazard.getSpeed() * deltaTime, 0);
        }
      }
    });
    
    // Remove hazards that are far off screen
    this.hazards = this.hazards.filter(hazard => {
      const pos = hazard.getPosition();
      return pos.x > -200 && pos.x < this.GAME_WIDTH + 200;
    });
  }

  private checkCollisions(): void {
    if (!this.frog) return;
    
    const frogPos = this.frog.getPosition();
    const frogRow = Math.floor(frogPos.y / this.GRID_SIZE);
    const laneConfig = this.LANE_CONFIG[13 - frogRow];
    
    if (laneConfig.type === 'road') {
      // Check vehicle collisions
      this.checkVehicleCollisions();
    } else if (laneConfig.type === 'water') {
      // Check if frog is on a log or turtle
      this.checkWaterSafety();
    } else if (laneConfig.type === 'goal') {
      // Check goal collisions
      this.checkGoalCollisions();
    }
  }

  private checkVehicleCollisions(): void {
    if (!this.frog) return;
    
    const frogBounds = this.frog.getBounds();
    
    for (const vehicle of this.vehicles) {
      if (vehicle.checkCollision(frogBounds)) {
        this.loseLife();
        return;
      }
    }
  }

  private checkWaterSafety(): void {
    if (!this.frog) return;
    
    const frogBounds = this.frog.getBounds();
    let isOnSafeObject = false;
    
    // Check if frog is on any log or turtle
    for (const logTurtle of this.logsTurtles) {
      if (logTurtle.checkCollision(frogBounds)) {
        isOnSafeObject = true;
        this.frog.setRiding(logTurtle);
        break;
      }
    }
    
    // Check if frog is on a safe hazard (like a crocodile with mouth closed)
    if (!isOnSafeObject) {
      for (const hazard of this.hazards) {
        if (hazard.checkCollision(frogBounds)) {
          isOnSafeObject = true;
          this.frog.setRiding(hazard);
          break;
        }
      }
    }
    
    // Check for dangerous hazard collisions
    for (const hazard of this.hazards) {
      if (hazard.checkDangerCollision(frogBounds)) {
        this.loseLife();
        return;
      }
    }
    
    // If not on safe object, lose life
    if (!isOnSafeObject) {
      this.frog.setRiding(null);
      this.loseLife();
    }
  }

  private checkGoalCollisions(): void {
    if (!this.frog) return;
    
    const frogPos = this.frog.getPosition();
    const goalIndex = Math.floor(frogPos.x / (this.GAME_WIDTH / 5)); // 5 lily pads
    
    if (this.goalManager.isGoalOpen(goalIndex)) {
      // Frog reached an open frog bay!
      this.goalManager.fillGoal(goalIndex);
      this.addScore(this.BASE_POINTS.FROG_BAY);
      this.addScore(Math.floor(this.gameTimer) * this.BASE_POINTS.TIME_BONUS); // Time bonus
      this.audioManager.playPowerUp();
      
      // Reset frog position
      this.resetFrogPosition();
      
      // Check if all frog bays are filled (wave complete)
      if (this.goalManager.allGoalsFilled()) {
        this.completeWave();
      }
    } else {
      // Frog bay already filled - frog dies
      this.loseLife();
    }
  }

  private checkWinConditions(): void {
    if (!this.frog) return;
    
    const frogPos = this.frog.getPosition();
    const currentRow = Math.floor(frogPos.y / this.GRID_SIZE);
    
    // Award points for forward progress
    if (currentRow < this.highestRowReached) {
      const progress = this.highestRowReached - currentRow;
      this.addScore(progress * this.BASE_POINTS.FORWARD_HOP);
      this.highestRowReached = currentRow;
    }
  }

  private checkExtraLife(): void {
    // Check if player earned extra life at 20,000 points
    const extraLivesEarned = Math.floor(this.score / this.EXTRA_LIFE_SCORE);
    const lastExtraLivesEarned = Math.floor(this.lastExtraLifeScore / this.EXTRA_LIFE_SCORE);
    
    if (extraLivesEarned > lastExtraLivesEarned) {
      this.lives += (extraLivesEarned - lastExtraLivesEarned);
      this.audioManager.playPowerUp();
      console.log(`Extra life! Score: ${this.score}, Lives: ${this.lives}`);
    }
    
    this.lastExtraLifeScore = this.score;
  }
  
  private updateBonusObjects(deltaTime: number): void {
    // Update lady frog spawn timer
    this.ladyFrogTimer += deltaTime;
    if (this.ladyFrogTimer >= this.LADY_FROG_SPAWN_TIME && !this.ladyFrog) {
      this.spawnLadyFrog();
      this.ladyFrogTimer = 0;
    }
    
    // Update lady frog timer
    if (this.ladyFrog) {
      this.ladyFrog.timer += deltaTime;
      if (this.ladyFrog.timer >= 10) { // Lady frog disappears after 10 seconds
        this.ladyFrog = null;
      }
    }
    
    // Update fly spawn timer
    this.flySpawnTimer += deltaTime;
    if (this.flySpawnTimer >= this.FLY_SPAWN_TIME && this.flies.length < 2) {
      this.spawnFly();
      this.flySpawnTimer = 0;
    }
    
    // Update flies
    this.flies.forEach(fly => {
      fly.timer += deltaTime;
    });
    
    // Remove expired flies (disappear after 8 seconds)
    this.flies = this.flies.filter(fly => fly.timer < 8);
    
    // Check bonus object collisions
    this.checkBonusCollisions();
  }
  
  private spawnLadyFrog(): void {
    // Spawn lady frog on a log in the river area
    const riverRows = [1, 2, 3, 4, 5, 6];
    const randomRow = riverRows[Math.floor(Math.random() * riverRows.length)];
    const randomX = Math.random() * (this.GAME_WIDTH - 32);
    
    this.ladyFrog = {
      x: randomX,
      y: randomRow * this.GRID_SIZE,
      active: true,
      timer: 0
    };
  }
  
  private spawnFly(): void {
    // Spawn fly anywhere in the game area
    const randomX = Math.random() * (this.GAME_WIDTH - 16);
    const randomY = Math.random() * (this.GAME_HEIGHT - 16);
    
    this.flies.push({
      x: randomX,
      y: randomY,
      active: true,
      timer: 0
    });
  }
  
  private checkBonusCollisions(): void {
    if (!this.frog) return;
    
    const frogBounds = this.frog.getBounds();
    
    // Check lady frog collision
    if (this.ladyFrog && this.ladyFrog.active) {
      const ladyBounds = {
        x: this.ladyFrog.x,
        y: this.ladyFrog.y,
        width: 32,
        height: 32
      };
      
      if (this.checkBoundingBoxCollision(frogBounds, ladyBounds)) {
        this.addScore(this.BASE_POINTS.LADY_FROG);
        this.audioManager.playPowerUp();
        this.ladyFrog = null;
        console.log('Lady frog bonus! +200 points');
      }
    }
    
    // Check fly collisions
    this.flies.forEach((fly, index) => {
      if (fly.active) {
        const flyBounds = {
          x: fly.x,
          y: fly.y,
          width: 16,
          height: 16
        };
        
        if (this.checkBoundingBoxCollision(frogBounds, flyBounds)) {
          this.addScore(this.BASE_POINTS.FLY_CATCH);
          this.audioManager.playPowerUp();
          this.flies.splice(index, 1);
          console.log('Fly caught! +200 points');
        }
      }
    });
  }
  
  private checkBoundingBoxCollision(rect1: Rectangle, rect2: Rectangle): boolean {
    return rect1.x < rect2.x + rect2.width &&
           rect1.x + rect1.width > rect2.x &&
           rect1.y < rect2.y + rect2.height &&
           rect1.y + rect1.height > rect2.y;
  }
  
  private spawnEntitiesAsNeeded(): void {
    // Respawn vehicles and logs/turtles that have gone off screen
    this.spawnVehicleIfNeeded();
    this.spawnLogTurtleIfNeeded();
  }

  private spawnVehicleIfNeeded(): void {
    for (let row = 8; row <= 12; row++) {
      const laneConfig = this.LANE_CONFIG[13 - row];
      if (laneConfig.type !== 'road') continue;
      
      const vehiclesInLane = this.vehicles.filter(v => {
        const pos = v.getPosition();
        return Math.floor(pos.y / this.GRID_SIZE) === row;
      });
      
      if (vehiclesInLane.length < 2) {
        // Spawn new vehicle at appropriate edge
        const spawnX = laneConfig.speed > 0 ? -100 : this.GAME_WIDTH + 100;
        const y = row * this.GRID_SIZE;
        const isLargeVehicle = Math.random() < 0.3;
        
        const vehicle = new Vehicle(
          `vehicle_${row}_${Date.now()}`,
          spawnX,
          y,
          laneConfig.speed * (1 + this.level * 0.05), // Gradual speed increase with level
          isLargeVehicle,
          this.renderManager,
          this.audioManager,
          this.GAME_WIDTH
        );
        
        this.vehicles.push(vehicle);
      }
    }
  }

  private spawnHazardsForLane(row: number, speed: number): void {
    // Only spawn hazards occasionally and on higher levels
    if (Math.random() > 0.3) return; // 30% chance to spawn hazard
    
    const hazardCount = 1; // One hazard per spawn
    const x = Math.random() * this.GAME_WIDTH;
    const y = row * this.GRID_SIZE;
    
    // Choose hazard type based on level
    let hazardType: HazardType = 'snake';
    if (this.level >= 5) {
      const types: HazardType[] = ['snake', 'otter', 'crocodile'];
      hazardType = types[Math.floor(Math.random() * types.length)];
    } else if (this.level >= 4) {
      hazardType = Math.random() < 0.5 ? 'snake' : 'otter';
    }
    
    const hazard = new Hazard(
      `hazard_${row}_${Date.now()}`,
      x,
      y,
      speed * 0.8, // Hazards move slightly slower
      hazardType,
      this.renderManager,
      this.audioManager,
      this.GAME_WIDTH
    );
    
    this.hazards.push(hazard);
  }
  
  private spawnLogTurtleIfNeeded(): void {
    for (let row = 1; row <= 6; row++) {
      const laneConfig = this.LANE_CONFIG[13 - row];
      if (laneConfig.type !== 'water') continue;
      
      const objectsInLane = this.logsTurtles.filter(lt => {
        const pos = lt.getPosition();
        return Math.floor(pos.y / this.GRID_SIZE) === row;
      });
      
      if (objectsInLane.length < 2) {
        // Spawn new log/turtle at appropriate edge
        const spawnX = laneConfig.speed > 0 ? -100 : this.GAME_WIDTH + 100;
        const y = row * this.GRID_SIZE;
        const isLog = (row % 2) === 1;
        
        const logTurtle = new LogTurtle(
          `logturtle_${row}_${Date.now()}`,
          spawnX,
          y,
          laneConfig.speed * (1 + this.level * 0.03), // Very gradual speed increase
          isLog,
          this.renderManager,
          this.audioManager,
          this.GAME_WIDTH
        );
        
        this.logsTurtles.push(logTurtle);
      }
    }
  }

  private resetFrogPosition(): void {
    if (!this.frog) return;
    
    const startX = Math.floor(this.GRID_COLS / 2) * this.GRID_SIZE;
    const startY = 13 * this.GRID_SIZE;
    
    this.frog.setPosition(startX, startY);
    this.frog.revive(); // Properly revive the frog
    this.frog.setRiding(null);
    this.currentRow = 13;
    this.highestRowReached = 13;
    this.gameTimer = this.maxTimer;
  }

  private completeWave(): void {
    // Award wave completion bonus
    this.addScore(this.BASE_POINTS.WAVE_COMPLETE);
    this.nextLevel();
    
    // Reset for next wave
    this.goalManager.reset();
    this.resetFrogPosition();
    
    // Increase difficulty
    this.maxTimer = Math.max(30, this.maxTimer - 5); // Reduce time limit
    console.log(`Wave ${this.level} complete! New time limit: ${this.maxTimer}s`);
  }

  public loseLife(): void {
    if (this.frog) {
      this.frog.die();
    }
    
    this.audioManager.playExplosion();
    
    super.loseLife(); // Call parent to handle lives decrement and game over
    
    if (!this.isGameOver) {
      // Reset frog position after a short delay
      setTimeout(() => {
        this.resetFrogPosition();
      }, 1000);
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen
    this.renderManager.clear('#000000');
    
    // Draw game area background
    this.renderGameArea();
    
    // Render lanes
    this.renderLanes();
    
    // Render vehicles
    this.vehicles.forEach(vehicle => {
      vehicle.renderWithOffset(ctx, this.OFFSET_X, this.OFFSET_Y);
    });
    
    // Render logs and turtles
    this.logsTurtles.forEach(logTurtle => {
      logTurtle.renderWithOffset(ctx, this.OFFSET_X, this.OFFSET_Y);
    });
    
    // Render hazards
    this.hazards.forEach(hazard => {
      hazard.renderWithOffset(ctx, this.OFFSET_X, this.OFFSET_Y);
    });
    
    // Render goals
    this.goalManager.render(this.OFFSET_X, this.OFFSET_Y);
    
    // Render frog
    if (this.frog) {
      this.frog.renderWithOffset(ctx, this.OFFSET_X, this.OFFSET_Y);
    }
    
    // Render bonus objects
    this.renderBonusObjects();
    
    // Render HUD
    this.renderHUD();
    
    // Render game over screen
    if (this.isGameOver) {
      this.renderGameOver();
    }
    
    // Render initial entry if showing
    if (this.showingInitialEntry && this.initialEntry) {
      this.initialEntry.render(ctx, this.config.width / 2, this.config.height / 2);
    }
  }

  private renderGameArea(): void {
    // Draw game area border
    this.renderManager.strokeRect(
      this.OFFSET_X - 2,
      this.OFFSET_Y - 2,
      this.GAME_WIDTH + 4,
      this.GAME_HEIGHT + 4,
      '#FFFFFF',
      2
    );
  }

  private renderLanes(): void {
    for (let row = 0; row < this.GRID_ROWS; row++) {
      const y = this.OFFSET_Y + row * this.GRID_SIZE;
      const laneConfig = this.LANE_CONFIG[13 - row];
      
      let color = '#000000';
      switch (laneConfig.type) {
        case 'safe':
          color = '#00AA00'; // Green
          break;
        case 'road':
          color = '#444444'; // Dark gray
          break;
        case 'water':
          color = '#0066CC'; // Blue
          break;
        case 'goal':
          color = '#00AA00'; // Green
          break;
      }
      
      this.renderManager.fillRect(
        this.OFFSET_X,
        y,
        this.GAME_WIDTH,
        this.GRID_SIZE,
        color
      );
      
      // Draw lane dividers
      if (row > 0) {
        this.renderManager.drawLine(
          this.OFFSET_X,
          y,
          this.OFFSET_X + this.GAME_WIDTH,
          y,
          '#FFFFFF',
          1
        );
      }
    }
  }

  protected renderHUD(): void {
    // Score
    this.renderManager.drawText(
      `SCORE: ${this.score.toString().padStart(6, '0')}`,
      20,
      20,
      '#FFFFFF',
      '16px "Courier New", monospace'
    );
    
    // Lives
    this.renderManager.drawText('LIVES: ', 20, 50, '#FFFFFF', '16px "Courier New", monospace');
    for (let i = 0; i < this.lives; i++) {
      this.renderManager.drawC64Char('🐸', 100 + i * 25, 48, '#00FF00', 1);
    }
    
    // Wave (Level)
    this.renderManager.drawText(
      `WAVE: ${this.level}`,
      this.config.width - 150,
      20,
      '#FFFFFF',
      '16px "Courier New", monospace'
    );
    
    // Timer
    const timerColor = this.gameTimer < 10 ? '#FF0000' : '#FFFFFF';
    this.renderManager.drawText(
      `TIME: ${Math.max(0, Math.ceil(this.gameTimer))}`,
      this.config.width - 150,
      50,
      timerColor,
      '16px "Courier New", monospace'
    );
    
    // Controls
    this.renderManager.drawText(
      'CONTROLS: Arrow Keys to Hop',
      20,
      this.config.height - 20,
      '#CCCCCC',
      '12px "Courier New", monospace'
    );
  }

  protected renderGameOver(): void {
    const centerX = this.config.width / 2;
    const centerY = this.config.height / 2;
    
    this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
    
    this.renderManager.drawTextCentered(
      'GAME OVER',
      centerX,
      centerY - 60,
      '#FFFFFF',
      '32px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `FINAL SCORE: ${this.score}`,
      centerX,
      centerY - 20,
      '#FFFFFF',
      '18px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `REACHED WAVE ${this.level}`,
      centerX,
      centerY + 10,
      '#CCCCCC',
      '16px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      'PRESS SPACE TO RESTART',
      centerX,
      centerY + 60,
      '#FFFFFF',
      '14px "Courier New", monospace'
    );
  }

  public handleInput(input: InputState): void {
    if (this.isGameOver) {
      if (this.showingInitialEntry) {
        return;
      }
      
      if (input.keys['Space'] || input.touch.gesture === 'tap') {
        this.restart();
      }
      return;
    }

    if (!this.frog) return;

    // Movement - Frogger uses discrete hops, not continuous movement
    if (input.keys['ArrowUp'] || input.keys['KeyW']) {
      this.frog.hop(0, -1);
    } else if (input.keys['ArrowDown'] || input.keys['KeyS']) {
      this.frog.hop(0, 1);
    } else if (input.keys['ArrowLeft'] || input.keys['KeyA']) {
      this.frog.hop(-1, 0);
    } else if (input.keys['ArrowRight'] || input.keys['KeyD']) {
      this.frog.hop(1, 0);
    }
  }

  private renderBonusObjects(): void {
    // Render lady frog
    if (this.ladyFrog && this.ladyFrog.active) {
      const x = this.OFFSET_X + this.ladyFrog.x;
      const y = this.OFFSET_Y + this.ladyFrog.y;
      this.renderLadyFrog(x, y);
    }
    
    // Render flies
    this.flies.forEach(fly => {
      if (fly.active) {
        const x = this.OFFSET_X + fly.x;
        const y = this.OFFSET_Y + fly.y;
        this.renderFly(x, y, fly.timer);
      }
    });
  }
  
  private renderLadyFrog(x: number, y: number): void {
    // Lady frog is pink/magenta colored
    const bodyColor = '#FF69B4'; // Hot pink
    const size = 24;
    const centerX = x + 16;
    const centerY = y + 16;
    
    // Main body (oval)
    this.renderManager.drawEllipse(
      centerX - size * 0.4,
      centerY - size * 0.3,
      size * 0.4,
      size * 0.3,
      bodyColor,
      true
    );
    
    // Eyes
    const eyeSize = size * 0.08;
    this.renderManager.drawEllipse(
      centerX - size * 0.15,
      centerY - size * 0.35,
      eyeSize,
      eyeSize,
      '#FFFF00',
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + size * 0.05,
      centerY - size * 0.35,
      eyeSize,
      eyeSize,
      '#FFFF00',
      true
    );
    
    // Legs
    const legSize = size * 0.06;
    
    // Front legs
    this.renderManager.drawEllipse(
      centerX - size * 0.3,
      centerY - size * 0.1,
      legSize,
      legSize,
      bodyColor,
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + size * 0.2,
      centerY - size * 0.1,
      legSize,
      legSize,
      bodyColor,
      true
    );
    
    // Back legs
    this.renderManager.drawEllipse(
      centerX - size * 0.25,
      centerY + size * 0.05,
      legSize,
      legSize,
      bodyColor,
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + size * 0.15,
      centerY + size * 0.05,
      legSize,
      legSize,
      bodyColor,
      true
    );
    
    // Add a small crown or bow to distinguish lady frog
    this.renderManager.fillRect(
      centerX - 3,
      centerY - size * 0.45,
      6,
      3,
      '#FFD700' // Gold
    );
  }
  
  private renderFly(x: number, y: number, timer: number): void {
    // Animate fly movement
    const buzz = Math.sin(timer * 20) * 2;
    const adjustedX = x + buzz;
    const adjustedY = y + Math.sin(timer * 15) * 1;
    
    // Fly body (small black dot)
    this.renderManager.drawEllipse(
      adjustedX + 6,
      adjustedY + 6,
      2,
      2,
      '#000000',
      true
    );
    
    // Wings (animated)
    const wingPhase = timer * 30;
    const wingOffset = Math.sin(wingPhase) * 2;
    
    // Left wing
    this.renderManager.drawEllipse(
      adjustedX + 3,
      adjustedY + 5 + wingOffset,
      3,
      1,
      'rgba(200, 200, 200, 0.7)',
      true
    );
    
    // Right wing
    this.renderManager.drawEllipse(
      adjustedX + 9,
      adjustedY + 5 - wingOffset,
      3,
      1,
      'rgba(200, 200, 200, 0.7)',
      true
    );
  }
  
  public cleanup(): void {
    super.cleanup();
    this.vehicles = [];
    this.logsTurtles = [];
    this.hazards = [];
    this.frog = null;
    this.ladyFrog = null;
    this.flies = [];
  }
}